import React, { useRef, useLayoutEffect } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import PropTypes from 'prop-types';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { Box, Button, InputLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import downArrow from '../../../../assets/dashboard-svg/down-arrow.svg';
import upArrow from '../../../../assets/dashboard-svg/up-arrow.svg';
import {
	convertToInternationalCurrencySystem,
	convertToTwoDecimal,
} from '../../../../helper/convertAmount';
import { colors } from '../../../../theme/color-palette';
import {
	GRID_DISTANCE,
	NETSALES,
	ONHAND_INVENTORY,
	INSTOCKINVENTORY,
	EXPLORE,
	IN_STOCK,
} from '../../../../constants/constant';
import {
	salesTooltipFormat,
	inventoryTooltipFormat,
	createMetricsTooltip,
	replenishmentTooltipFormat,
	wosTooltipFormat,
} from './util';
import styles from './styles';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { useHistory } from 'react-router-dom';
import { saveKpiName } from '../../../../redux/actions/salesActions';
import { useDispatch, useSelector } from 'react-redux';
import analytics from '../../../../helper/analytics';
import tracker from '../../../../constants/tracker';

const {
	FONT_COLOR,
	PURPLE_INACTIVE,
	PURPLE,
	COLUMN_SERIES,
	OFF_WHITE,
	POSITIVE_GROWTH_COLOR,
	NEGATIVE_GROWTH_COLOR,
	LIGHT_PURPLE,
} = colors;

// chart styles
const useStyles = makeStyles(() => ({
	lineChart: {
		height: (P) => P.chartHeight,
		width: (P) => P.chartWidth,
	},
}));

am4core.options.commercialLicense = true;

const bigNumberPrefix = [
	{ number: 1e3, suffix: 'k' },
	{ number: 1e6, suffix: 'm' },
	{ number: 1e9, suffix: 'b' },
	{ number: 1e12, suffix: 't' },
	{ number: 1e15, suffix: 'p' },
	{ number: 1e18, suffix: 'e' },
	{ number: 1e21, suffix: 'z' },
	{ number: 1e24, suffix: 'y' },
];

/* Common component for line chart */
const LineChart = (props) => {
	const {
		chartData,
		chartValue,
		chartWidth,
		chartHeight,
		chartName,
		weekFilter,
		xAxisData,
		yAxisMinValue,
		isCompositChart,
		isInsight,
		showExploreButton,
		isAmount,
		chartTitle,
		inStock,
		showWeekDates,
		isReplenishment,
		isWOS,
		tabName,
	} = props;
	const chartRef = useRef(null);
	const classes = useStyles({
		...props,
		chartHeight,
		chartWidth,
	});

	const style = styles();
	const navigate = useHistory();
	const dispatch = useDispatch();
	const isFullCompanyView = useSelector((state) => state.salesReducer.isFCV);
	const isColumn = weekFilter <= 0 && chartData?.length <= 1;

	const handleChange = () => {
		navigate.push('/drilldown');
		if (chartTitle) {
			dispatch(
				saveKpiName(
					chartTitle === `${IN_STOCK}%` ? INSTOCKINVENTORY : chartTitle
				)
			);
		} else {
			dispatch(saveKpiName(chartName));
		}
		analytics.trackEventInfo({
			type: tracker.EVENTS.CLICK,
			actionOn: tracker.ACTION_ON.EXPLORE_BUTTON,
			location: chartTitle || chartName,
		});
	};

	const getTooltipvalue = (value) => {
		const isNegative = Math.sign(value);
		if (isWOS) {
			return value <= 1 ? `${value} week` : `${value} weeks`;
		} else {
			return (
				(isNegative === -1 &&
					`- ${isAmount && !inStock && !isReplenishment ? '$' : ''}${Math.abs(
						parseFloat(convertToInternationalCurrencySystem(value))
					)}`) ||
				`${
					isAmount && !inStock && !isReplenishment ? '$' : ''
				}${convertToInternationalCurrencySystem(value)}`
			);
		}
	};

	const getWOSWeek = (item, weekFilter) => {
		if (weekFilter === 0) {
			return 'week to date';
		} else if (weekFilter === 1) {
			return 'last week';
		} else {
			return `Week ${item?.wmWkNbr}`;
		}
	};

	const parseData = (data) => {
		return data?.map((item) => {
			const weekWithNo = isWOS
				? getWOSWeek(item, weekFilter)
				: `Week ${item.wmWkNbr}`;
			const trendDiffPerc = item.yoy;
			const trendColorPerc =
				trendDiffPerc > 0 ? POSITIVE_GROWTH_COLOR : NEGATIVE_GROWTH_COLOR;
			const trendArrowComp = trendDiffPerc > 0 ? upArrow : downArrow;
			const colorPerc =
				trendDiffPerc > 0 ? POSITIVE_GROWTH_COLOR : NEGATIVE_GROWTH_COLOR;
			const arrowComp = trendDiffPerc > 0 ? upArrow : downArrow;
			return {
				...item,
				wmYrWkNbr: weekWithNo,
				diff_perc:
					trendDiffPerc === -999999.0
						? 'NA'
						: `${convertToTwoDecimal(trendDiffPerc, true)}%`,
				trend_diff_perc: Math.abs(trendDiffPerc),
				color_perc: colorPerc,
				arrow_comp: arrowComp,
				trend_color_perc: trendColorPerc,
				trend_arrow_comp: trendArrowComp,
				trend_ty_value: getTooltipvalue(item.tyValue),
				trend_ly_value: getTooltipvalue(item.lyValue),
				trend_wos: getTooltipvalue(item?.wos),
				trend_net_sales_value: getTooltipvalue(item.tyValue),
				trend_on_hand_value: getTooltipvalue(item.lyValue),
				trend_wkStartDt: item.wkStartDt,
				trend_wkEndDt: item.wkEndDt,
			};
		});
	};

	useLayoutEffect(() => {
		am4core.useTheme(am4themes_animated);
		let lineChart = am4core.create(chartName, am4charts.XYChart);
		lineChart.hiddenState.properties.opacity = 0;

		lineChart.data = parseData(chartData);
		let dataCount;
		if (lineChart?.data.length < 8) {
			dataCount = 1;
		} else {
			dataCount = lineChart?.data?.length < 15 ? 2 : 5;
		}

		// Create axes
		let categoryAxis;
		if ([1].includes(weekFilter) && !isWOS) {
			categoryAxis = lineChart.xAxes.push(new am4charts.DateAxis());
			categoryAxis.dateFormats.setKey('day', 'MM/dd');
			categoryAxis.periodChangeDateFormats.setKey('day', 'MM/dd');
			categoryAxis.gridIntervals.setAll([
				{ timeUnit: 'day', count: dataCount },
			]);
		} else {
			categoryAxis = lineChart.xAxes.push(new am4charts.CategoryAxis());
			categoryAxis.dataFields.category = xAxisData.nonDate;

			if (
				weekFilter === 0 ||
				weekFilter === 1 ||
				(!isWOS && chartData?.length === 1 && weekFilter === -1)
			) {
				categoryAxis.renderer.inversed = true;
			}
		}

		const readingDataPointFormat = "'{categoryX}' '{valueY}'";

		//Label Formatting
		categoryAxis.renderer.minGridDistance = 0;
		categoryAxis.renderer.labels.template.fontSize = 12;
		categoryAxis.renderer.labels.template.fill = am4core.color(FONT_COLOR);
		categoryAxis.renderer.grid.template.location = 'none';
		categoryAxis.renderer.grid.template.disabled = true;

		const valueAxis = lineChart.yAxes.push(new am4charts.ValueAxis());
		valueAxis.renderer.opposite = isCompositChart ? true : false;
		valueAxis.renderer.labels.template.fontSize = 12;
		valueAxis.renderer.labels.template.fill = am4core.color(FONT_COLOR);
		if (yAxisMinValue !== undefined) {
			valueAxis.min = yAxisMinValue;
		}

		let valueAxisLeft;
		if (isCompositChart) {
			valueAxisLeft = lineChart.yAxes.push(new am4charts.ValueAxis());
			valueAxisLeft.renderer.labels.template.fontSize = 12;
			valueAxisLeft.renderer.labels.template.fill = am4core.color(FONT_COLOR);
			valueAxisLeft.cursorTooltipEnabled = false;
			valueAxisLeft.syncWithAxis = valueAxis;

			const topContainer = lineChart.chartContainer.createChild(
				am4core.Container
			);
			topContainer.layout = 'absolute';
			topContainer.align = 'center';
			topContainer.toBack();
			topContainer.paddingBottom = 15;
			topContainer.width = am4core.percent(100);

			const axisTitleLeft = topContainer.createChild(am4core.Label);
			axisTitleLeft.text = 'Net Sales($)';
			axisTitleLeft.fontSize = '10px';
			axisTitleLeft.fontWeight = 400;
			axisTitleLeft.align = 'left';
			axisTitleLeft.marginLeft = am4core.percent(6);

			const axisTitleRight = topContainer.createChild(am4core.Label);
			axisTitleRight.text = 'Inventory($)';
			axisTitleRight.fontSize = '10px';
			axisTitleRight.fontWeight = 400;
			axisTitleRight.align = 'right';
			axisTitleRight.marginRight = am4core.percent(6);
		}
		const lymaxValue = Math.max(...[...chartData.map((i) => i.lyValue)]);
		const tymaxValue = Math.max(...[...chartData.map((i) => i.tyValue)]);
		const isLessValue = lymaxValue < 100 || tymaxValue < 100;

		if (isAmount) {
			valueAxis.numberFormatter = new am4core.NumberFormatter();

			// Configure number formatter
			let postfixForYaxis = '';
			if (isWOS) {
				postfixForYaxis = '#wk';
			} else if (isLessValue) {
				postfixForYaxis = '$#.##';
			} else {
				postfixForYaxis = '$#.##a';
			}

			valueAxis.numberFormatter.numberFormat = postfixForYaxis;
			valueAxis.numberFormatter.bigNumberPrefixes = bigNumberPrefix;
			if (isCompositChart) {
				valueAxisLeft.numberFormatter = new am4core.NumberFormatter();
				valueAxisLeft.numberFormatter.numberFormat = isLessValue
					? '$#.##'
					: '$#.##a';
				valueAxisLeft.numberFormatter.bigNumberPrefixes = bigNumberPrefix;
			}
		} else {
			valueAxis.numberFormatter = new am4core.NumberFormatter();
			// Configure number formatter
			valueAxis.numberFormatter.numberFormat = '#.##a';
			if (isReplenishment) {
				valueAxis.numberFormatter.numberFormat = '#.##';
			}
			valueAxis.numberFormatter.bigNumberPrefixes = bigNumberPrefix;
		}

		// For labels going out of charts in column charts
		if (isColumn) {
			valueAxis.extraMax = 0.1;
		}

		// configurartions for composite charts
		if (isCompositChart && weekFilter > 0) {
			valueAxis.renderer.line.strokeOpacity = 1;
			valueAxis.renderer.line.strokeWidth = 0;
			valueAxis.renderer.grid.template.disabled = true;

			valueAxisLeft.renderer.line.strokeOpacity = 0;
			valueAxisLeft.renderer.line.strokeWidth = 0;
		}
		// removed category axis tooltip as per VQA comments
		categoryAxis.cursorTooltipEnabled = false;
		valueAxis.cursorTooltipEnabled = false;
		if (weekFilter > 0 && isCompositChart) {
			valueAxisLeft.cursorTooltipEnabled = false;
		}

		/**
		 * Create series
		 * @param field - valueY on the chart
		 * @param name - name of the chart
		 * @param color - color of the line chart
		 * @param dasharray - style of the line chart
		 * @return series
		 */

		let count = 0;
		const tooltip = () => {
			if (isCompositChart) {
				return inventoryTooltipFormat(weekFilter, showWeekDates);
			} else if (isInsight) {
				return createMetricsTooltip();
			} else if (isReplenishment) {
				return replenishmentTooltipFormat('%');
			} else if (isWOS) {
				return wosTooltipFormat(tabName);
			} else {
				return salesTooltipFormat(
					inStock ? '%' : '',
					weekFilter,
					showWeekDates
				);
			}
		};
		function createLineSeries(field, name, color, dasharray) {
			if (isReplenishment && field === 'lyValue') {
				return null;
			}
			const series = lineChart.series.push(new am4charts.LineSeries());
			series.itemReaderText = readingDataPointFormat;
			series.focusable = true;
			series.dataFields.valueY = field;
			if ([1].includes(weekFilter)) {
				series.dataFields.dateX = xAxisData.date;
				if (count === 0) {
					series.tooltipHTML = tooltip();
				}
				count++;
			} else {
				series.dataFields.categoryX = xAxisData.nonDate;
				if (count === 0) {
					series.tooltipHTML = tooltip();
				}
				count++;

				categoryAxis.renderer.minGridDistance = 100;
				if (weekFilter > -1) {
					categoryAxis.startLocation = GRID_DISTANCE[weekFilter]
						? GRID_DISTANCE[weekFilter][0]
						: 0;
					categoryAxis.endLocation = GRID_DISTANCE[weekFilter]
						? GRID_DISTANCE[weekFilter][1]
						: 1;
				} else {
					if (chartData?.length <= 4) {
						categoryAxis.startLocation = 0;
						categoryAxis.endLocation = 1;
					} else if (chartData?.length <= 13) {
						categoryAxis.startLocation = -1;
						categoryAxis.endLocation = 1;
					} else if (chartData?.length <= 26) {
						categoryAxis.startLocation = -2;
						categoryAxis.endLocation = 2;
					} else {
						categoryAxis.startLocation = -5;
						categoryAxis.endLocation = 2;
					}
				}
			}
			series.name = name;
			series.strokeWidth = 2;
			series.zIndex = 10;
			series.tooltip.pointerOrientation = 'horizontal';
			series.tooltip.getFillFromObject = false;
			series.tooltip.background.fill = am4core.color(OFF_WHITE);

			if (color) {
				series.stroke = am4core.color(color);
			}

			if (dasharray) {
				series.strokeDasharray = dasharray;
			}
			if (isCompositChart) {
				createBullets(series, color);
				series.yAxis = field === 'tyValue' ? valueAxisLeft : valueAxis;
			} else {
				const bullet = series.bullets.push(new am4core.Circle());
				bullet.radius = 1;
				bullet.focusable = true;
				bullet.fillOpacity = 0;
				bullet.strokeWidth = 0;
				bullet.readerTitle = '{categoryX} {valueY}';
			}

			return series;
		}

		function createBullets(series, color) {
			const bullet = series.bullets.push(new am4charts.CircleBullet());
			bullet.readerTitle = '{categoryX} {valueY}';
			bullet.radius = 5;
			bullet.strokeWidth = 1;
			bullet.circle.fill = am4core.color('#fff');
			bullet.stroke = am4core.color(
				color === PURPLE ? PURPLE_INACTIVE : '#ECE9F3'
			);
			bullet.fillOpacity = 0;
			bullet.strokeOpacity = 0;

			const bullet1 = bullet.createChild(am4core.Circle);
			bullet1.fill = am4core.color(color);
			bullet1.radius = 2.5;
			bullet1.strokeWidth = 0;

			const bulletState = bullet.states.create('hover');
			bulletState.properties.fillOpacity = 1;
			bulletState.properties.strokeOpacity = 1;

			return bullet;
		}

		function createColomnSeries(field, name, color, isUnit = false) {
			const series1 = lineChart.series.push(new am4charts.ColumnSeries());
			series1.itemReaderText = readingDataPointFormat;
			series1.dataFields.valueY = field;
			if ([1].includes(weekFilter) && !isWOS) {
				series1.dataFields.dateX = xAxisData.date;
				if (count === 0) {
					series1.tooltipHTML = tooltip();
				}
				count++;
			} else {
				series1.dataFields.categoryX = xAxisData.nonDate;
				if (count === 0 && weekFilter !== 0) {
					series1.tooltipHTML = tooltip();
				}
				count++;

				categoryAxis.renderer.minGridDistance = 100;
				categoryAxis.startLocation = GRID_DISTANCE[weekFilter]
					? GRID_DISTANCE[weekFilter][0]
					: 0;
				categoryAxis.endLocation = GRID_DISTANCE[weekFilter]
					? GRID_DISTANCE[weekFilter][1]
					: 1;
			}
			series1.name = name;
			series1.fill = am4core.color(color);
			series1.strokeWidth = 0;
			series1.clustered = weekFilter <= 0 ? true : false;
			series1.yAxis =
				weekFilter <= 0 || (weekFilter < 2 && isWOS)
					? valueAxis
					: valueAxisLeft;

			series1.columns.template.width = isWOS ? 80 : am4core.percent(40);
			categoryAxis.renderer.cellStartLocation = 0.2;
			categoryAxis.renderer.cellEndLocation = 0.8;
			series1.cursorTooltipEnabled = false;

			if (weekFilter <= 0 || (weekFilter < 2 && isWOS)) {
				const valueLabel = series1.bullets.push(new am4charts.LabelBullet());
				valueLabel.label.text = isWOS || isUnit ? '{valueY}' : '$' + '{valueY}';
				valueLabel.label.dy = -10;
				valueLabel.label.truncate = false;
				valueLabel.label.hideOversized = false;
				valueAxis.extraMax = 0.2;
			}
		}

		function createBarSeries(field, isOpposite, color, name, weekFilter) {
			const series1 = lineChart.series.push(new am4charts.ColumnSeries());
			series1.itemReaderText = readingDataPointFormat;
			series1.columns.template.hoverOnFocus = true;
			series1.dataFields.valueY = field;
			series1.dataFields.categoryX = weekFilter === -1 ? 'wmYrWkNbr' : 'busDt';
			series1.fill = am4core.color(color);
			series1.strokeWidth = 0;
			series1.clustered = true;
			series1.name = name;

			const valueAxis = lineChart.yAxes.push(new am4charts.ValueAxis());
			valueAxis.renderer.opposite = isOpposite;
			valueAxis.renderer.labels.template.fontSize = 12;
			valueAxis.renderer.labels.template.fill = am4core.color(FONT_COLOR);

			if (!isOpposite) {
				valueAxis.renderer.line.strokeOpacity = 0;
				valueAxis.renderer.grid.template.disabled = true;
			} else {
				valueAxis.renderer.line.strokeOpacity = 0;
				valueAxis.renderer.line.strokeWidth = 0;
			}

			series1.yAxis = valueAxis;
			series1.columns.template.width = am4core.percent(15);

			const valueLabel = series1.bullets.push(new am4charts.LabelBullet());
			valueLabel.label.text = '$' + '{valueY}';
			valueLabel.label.dy = -5;
			valueLabel.label.truncate = false;
			valueLabel.label.hideOversized = false;
			categoryAxis.renderer.cellStartLocation = 0.2;
			categoryAxis.renderer.cellEndLocation = 0.8;
			valueAxis.cursorTooltipEnabled = false;
			series1.cursorTooltipEnabled = false;
			if (isAmount) {
				valueAxis.numberFormatter = new am4core.NumberFormatter();
				valueAxis.numberFormatter.numberFormat = '$#.##a';
				valueAxis.numberFormatter.bigNumberPrefixes = bigNumberPrefix;
			}
			return series1;
		}

		if (weekFilter <= 0 && isCompositChart && isColumn) {
			createBarSeries(
				'lyValue',
				true,
				LIGHT_PURPLE,
				ONHAND_INVENTORY,
				weekFilter
			);
			createBarSeries('tyValue', false, COLUMN_SERIES, NETSALES, weekFilter);
		} else {
			chartValue.forEach((item) => {
				!item.chartType || item.chartType === 'Line'
					? createLineSeries(item.value, item.name, item.color, item.chartLine)
					: createColomnSeries(item.value, item.name, item.color, item.isUnit);
			});
		}

		// setting legends
		lineChart.legend = new am4charts.Legend();
		lineChart.legend.disabled = isWOS;
		lineChart.legend.focusable = false;
		lineChart.legend.labels.template.fontSize = 12;
		lineChart.legend.labels.template.fill = am4core.color(FONT_COLOR);
		const markerTemplate = lineChart.legend.markers.template;
		markerTemplate.width = 24;
		markerTemplate.height = 8;
		// setting cursor
		lineChart.cursor = new am4charts.XYCursor();
		lineChart.cursor.lineY.disabled = true;
		lineChart.legend.reverseOrder = true;
		lineChart.focusable = true;
		// Disable toggle on legends
		lineChart.legend.itemContainers.template.clickable = false;
		lineChart.legend.itemContainers.template.focusable = false;
		lineChart.legend.itemContainers.template.cursorOverStyle =
			am4core.MouseCursorStyle.default;
		lineChart.zoomOutButton.disabled = true;

		chartRef.current = lineChart;
		return () => {
			lineChart.dispose();
		};
	}, [chartData, chartValue]);

	return (
		<>
			<Box id={chartName} className={classes.lineChart}></Box>
			{isFullCompanyView && showExploreButton && (
				<Box className={style.buttonContainer}>
					<Button
						className={style.button}
						onClick={handleChange}
						endIcon={<ArrowForwardIosIcon className={style.buttonIcon} />}>
						<InputLabel className={style.buttonLabel}>{EXPLORE}</InputLabel>
					</Button>
				</Box>
			)}
		</>
	);
};

LineChart.defaultProps = {
	chartData: [],
	chartHeight: '326px',
	chartWidth: '444px',
	chartValue: [],
	chartName: 'LineChart',
	weekFilter: '',
	xAxisData: {},
	isCompositChart: false,
	isInsight: false,
	showExploreButton: true,
	isAmount: true,
	isReplenishment: false,
	isWOS: false,
	tabName: 'On-Hand',
};

LineChart.propTypes = {
	chartData: PropTypes.arrayOf(PropTypes.shape({})),
	chartHeight: PropTypes.string,
	chartWidth: PropTypes.string,
	chartValue: PropTypes.arrayOf(PropTypes.shape({})),
	chartName: PropTypes.string,
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	xAxisData: PropTypes.shape({
		date: PropTypes.string,
		nonDate: PropTypes.string,
	}),
	yAxisMinValue: PropTypes.number,
	isCompositChart: PropTypes.bool,
	isInsight: PropTypes.bool,
	showExploreButton: PropTypes.bool,
	isAmount: PropTypes.bool,
	chartTitle: PropTypes.string,
	inStock: PropTypes.bool,
	showWeekDates: PropTypes.bool,
	isReplenishment: PropTypes.bool,
	isWOS: PropTypes.bool,
	tabName: PropTypes.string,
};

export default LineChart;
